/* eslint-disable no-underscore-dangle */
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Define the type for a single job
export interface JobInfo {
  postedAt: string | number | Date;
  timestamp: string | number | Date;
  _id: string;
  jobtitle: string;
  location: string;
  isFree: boolean;
  verified: boolean;
  jobdescription: string;
  joboffer: string;
  jobcategory: string;
  distance?: string;
  contact: string;
  createdAt?: string;
  startdate: any;
  amountType: string;
}

interface AddedJob {
  token: string | null;
  formState: JobInfo;
  file: File | null;
}

// Define the type for removing a job
interface RemoveJob {
  token: string | null;
  jobId: string;
}

// Thunk to fetch all jobs
export const fetchJobs = createAsyncThunk(
  'jobs/fetchJobs',
  async (token: string | null) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_JOBS_URL}/jobs/jobs`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  },
);

// Thunk to add a new job
export const addJob = createAsyncThunk(
  'jobs/addJob',
  async (newJob: AddedJob) => {
    const { token, formState, file } = newJob;
    const response = await axios.post(
      `${process.env.REACT_APP_API_JOBS_URL}/jobs/postJob`,
      formState,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (file) {
      const formData = new FormData();
      formData.append('jobImage', file);
      try {
        const res = await axios.put(
          `${process.env.REACT_APP_API_JOBS_URL}/jobs/job/${response.data.data._id}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
     
        return res.data;
      } catch (err) {
        console.error('Error uploading post file');
      }
    }

    return response.data;
  },
);

// Thunk to delete a job
export const deleteJob = createAsyncThunk(
  'jobs/deleteJob',
  async (jobRemove: RemoveJob) => {
    const { token, jobId } = jobRemove;
    await axios.delete(
      `${process.env.REACT_APP_API_JOBS_URL}/jobs/job/${jobId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
  },
);
export const removeJob = createAsyncThunk(
  'jobs/removeJob',
  async ({ jobId, token }: RemoveJob, thunkAPI) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_JOBS_URL}/jobs/remove-job/${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      return response.data.jobId; 
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || 'Failed to remove job'
      );
    }
  }
);

// Thunk to update a job
export const updateJob = createAsyncThunk(
  'jobs/updateJob',
  async (updatedJob: AddedJob) => {
    const { token, formState, file } = updatedJob;
    const jobId = formState._id;

    const response = await axios.put(
      `${process.env.REACT_APP_API_JOBS_URL}/jobs/job/${jobId}`,
      formState,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (file) {
      const formData = new FormData();
      formData.append('jobImage', file);

      try {
        await axios.put(
          `${process.env.REACT_APP_API_JOBS_URL}/jobs/job/${jobId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      } catch (err) {
        console.error('Error uploading post file');
      }
    }
  
    return response.data;
  },
);

// Thunk to fetch all jobs for a specific user
export const fetchUserJobs = createAsyncThunk(
  'jobs/fetchUserJobs',
  async (userId: string, { getState }) => {
    const state = getState();
    const { token } = (state as any).auth;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_JOBS_URL}/jobs/jobs/user-posts/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error:any) {
      console.error('Error fetching user jobs:', error.message);
      throw error;
    }
  },
);
